import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Adam Intop - `application/controllers/Assistant.php`, cap cu cap, la
 * `/api/v2/assistant/<numele vechi>`. Raspunsurile sunt cele pe care le citea
 * `od_ai.js`; doua deosebiri fata de portal:
 *
 *  - conversatia aleasa din istoric NU mai sta in sesiune (v4 e fara stare):
 *    dupa `openConversation` sertarul o trimite ca `conversation_id` cu fiecare
 *    cerere despre "conversatia curenta" (history, ask, stream, session...);
 *  - `stream` curge ca SSE (`data: {...,"type"}`), sau raspunde JSON
 *    `{stream:false}` cand nu poate curge si fereastra cade pe `ask`.
 */

export interface AiStep {
  tool: string
  args?: string
}

/**
 * O linie din fluxul `stream`. `step` poarta, pe langa `tool`, optional
 * `command` si `device_id` cand unealta e pe masina (run_command, remediate,
 * propose_change); `step_end` vine dupa unealta, cu `ms` si randul lasat
 * (`command_id` / `draft_id`), plus `error` cand unealta a refuzat - vezi
 * `DirectBrain::stepFields()` / `stepEndFields()`. Iesirea masinii NU vine pe
 * aici: se citeste din sesiune.
 */
export interface AiStreamEvent {
  type: string
  text?: string
  tool?: string
  command?: string
  device_id?: number
  command_id?: number
  draft_id?: number
  ms?: number
  error?: boolean
}

export interface AiMessage {
  id: number
  role: 'user' | 'assistant'
  text: string
  at: string
  steps: AiStep[]
}

export interface AiHistory {
  messages: AiMessage[]
  running: boolean
  progress: { text: string; note: string } | null
  conversation_id: number
  mode: string
}

export interface AiTier {
  key: string
  label: string
  model?: string
  current?: boolean
  usable?: boolean
  why?: string
}

/** O treapta de mod din meniul sertarului - `assistant/modes`. */
export interface AiMode {
  key: 'problem' | 'change' | 'incident' | 'full'
  label: string
  say: string
}

export interface AiRun {
  conversation_id: number
  device_id: number
  where: string
  question: string
  seconds: number
  state: 'running' | 'interrupted'
}

export interface AiConversation {
  id: number
  title: string
  started?: string
  device_id?: number
  device_name?: string
  /** 1 = raspuns automat la o alarma (user_id = 0), al firmei, nu al omului. */
  system?: number
  [key: string]: unknown
}

export interface AiDevice {
  id: number
  name: string
  os: string
}

export interface AiSessionCommand {
  id: number
  command: string
  at: string
  device: string
  device_id: number
  state: string
  output: string
}

/** Scopul discutiei: flota, sau o masina a firmei (verificata pe server). */
export interface AiScope {
  scope: 'all' | 'device'
  device_id: number
  conversation_id: number
}

function scopeFields(scope: AiScope): Record<string, PostValue> {
  const out: Record<string, PostValue> = {
    scope: scope.scope,
    device_id: scope.device_id,
  }
  if (scope.conversation_id > 0) out.conversation_id = scope.conversation_id
  return out
}

function scopeQuery(scope: AiScope): string {
  const q = new URLSearchParams({
    scope: scope.scope,
    device_id: String(scope.device_id),
  })
  if (scope.conversation_id > 0) q.set('conversation_id', String(scope.conversation_id))
  return q.toString()
}

export function aiTiersQuery() {
  return queryOptions({
    queryKey: ['assistant', 'tiers'],
    queryFn: () => apiGet<{ tiers: AiTier[]; user_id: number }>('/assistant/tiers'),
    staleTime: 5 * 60 * 1000,
  })
}

export function aiModesQuery() {
  return queryOptions({
    queryKey: ['assistant', 'modes'],
    queryFn: () => apiGet<{ modes: AiMode[] }>('/assistant/modes'),
    staleTime: 5 * 60 * 1000,
  })
}

export function aiHistory(scope: AiScope) {
  return apiGet<AiHistory>(`/assistant/history?${scopeQuery(scope)}`)
}

export function aiPending(since: number) {
  return apiGet<{
    last_assistant_id: number
    direct_commands: number
    unseen: number
  }>(`/assistant/pending?since=${since}`)
}

/** `parallel`: cati Adam poate tine omul in lucru deodata (implicit 1; lipsa = 1). */
export function aiRunning() {
  return apiGet<{ runs: AiRun[]; parallel?: number }>('/assistant/running')
}

export function aiAsk(scope: AiScope, message: string, tier: string) {
  return apiPost<{ text?: string; steps?: AiStep[]; error?: string }>('/assistant/ask', { ...scopeFields(scope), message, tier })
}

/**
 * Fluxul: `fetch` pe POST, citit bucata cu bucata. Cand serverul raspunde JSON
 * (nu poate curge), `onFallback` primeste corpul si fereastra cade pe `ask`.
 */
export async function aiStream(
  scope: AiScope,
  message: string,
  tier: string,
  signal: AbortSignal,
  onEvent: (event: AiStreamEvent) => void,
): Promise<{ streamed: boolean; fallback?: Record<string, unknown> }> {
  const body = new URLSearchParams()
  for (const [k, v] of Object.entries({ ...scopeFields(scope), message, tier })) body.set(k, String(v))
  const response = await fetch('/api/v2/assistant/stream', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'text/event-stream, application/json',
    },
    body,
    signal,
  })
  const type = response.headers.get('Content-Type') ?? ''
  if (!type.includes('text/event-stream')) {
    let fallback: Record<string, unknown> = { stream: false }
    try {
      fallback = (await response.json()) as Record<string, unknown>
    } catch {
      /* corpul nu era JSON; ramane {stream:false} */
    }
    if (response.status === 403)
      fallback = {
        stream: false,
        text: 'You do not have access to this action.',
        error: 'forbidden',
      }
    return { streamed: false, fallback }
  }
  const reader = response.body?.getReader()
  if (!reader) return { streamed: false, fallback: { stream: false } }
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let cut = buffer.indexOf('\n\n')
    while (cut >= 0) {
      const chunk = buffer.slice(0, cut)
      buffer = buffer.slice(cut + 2)
      for (const line of chunk.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            onEvent(JSON.parse(line.slice(6)) as AiStreamEvent)
          } catch {
            /* o linie stricata nu opreste fluxul */
          }
        }
      }
      cut = buffer.indexOf('\n\n')
    }
  }
  return { streamed: true }
}

export function aiCancel(scope: AiScope, conversationId = 0) {
  const fields = scopeFields(scope)
  if (conversationId > 0) fields.conversation_id = conversationId
  return apiPost<{ ok: boolean }>('/assistant/cancel', fields)
}

export function aiDismissRun(conversationId: number) {
  return apiPost<{ ok: boolean; error?: string }>('/assistant/dismissRun', {
    conversation_id: conversationId,
  })
}

export function aiReset(scope: AiScope) {
  return apiPost<{ ok: boolean; conversation_id: number }>('/assistant/reset', {
    scope: scope.scope,
    device_id: scope.device_id,
  })
}

export function aiTranscript(scope: AiScope) {
  return apiGet<{
    ok: boolean
    filename?: string
    text?: string
    error?: string
  }>(`/assistant/transcript?${scopeQuery(scope)}`)
}

export function aiToTicket(scope: AiScope, subject = '') {
  return apiPost<{ ok: boolean; ticket_id?: number; error?: string }>('/assistant/toTicket', { ...scopeFields(scope), subject })
}

export function aiDevices(q: string) {
  return apiGet<{ devices: AiDevice[] }>(`/assistant/devices?q=${encodeURIComponent(q)}`)
}

export function aiCommandOutput(id: number) {
  return apiGet<{
    ok: boolean
    state?: string
    output?: string
    error?: string
  }>(`/assistant/commandOutput?id=${id}`)
}

export function aiSession(scope: AiScope) {
  return apiGet<{ commands: number[]; changes: number[] }>(`/assistant/session?${scopeQuery(scope)}`)
}

export function aiSessionCommands(scope: AiScope) {
  return apiGet<{ rows: AiSessionCommand[] }>(`/assistant/sessionCommands?${scopeQuery(scope)}`)
}

export function aiConversations() {
  return apiGet<{ conversations: AiConversation[] }>('/assistant/conversations')
}

export function aiOpenConversation(conversationId: number) {
  return apiPost<{
    ok: boolean
    conversation_id?: number
    messages?: AiMessage[]
    device_id?: number
    error?: string
  }>('/assistant/openConversation', { conversation_id: conversationId })
}

/* ----- ecranele ----- */

export interface AiActivityRow {
  id: number
  at: string
  device_id: number
  device_name: string
  severity: string
  message: string
  who: string
  kind: string
  source_table: string
  source_id: number
  conversation_id: number
}

export interface AiActivityPage {
  rows: AiActivityRow[]
  counts: Record<string, number>
  kinds: Record<string, [string, string, string]>
  filters: { kind: string; device_id: number; days: number }
  agentMode: { mode: string; [key: string]: unknown }
}

export function aiActivityQuery(days: number, kind: string, deviceId: number) {
  const q = new URLSearchParams({
    days: String(days),
    kind,
    device_id: String(deviceId),
  })
  return queryOptions({
    queryKey: ['assistant', 'activity', days, kind, deviceId],
    queryFn: () => apiGet<AiActivityPage>(`/assistant/activity?${q.toString()}`),
  })
}

export interface AiSchedule {
  ID: number
  title: string
  question: string
  device_id: number
  device_name: string | null
  cadence: 'daily' | 'weekly' | 'monthly'
  run_hour: number
  run_minute: number
  weekday: number
  month_day: number
  recipients: string
  enabled: number
  next_run_at: string | null
  last_run_at: string | null
  last_status: string | null
  last_conversation_id: number
}

export interface AiSchedulesPage {
  ready: boolean
  rows: AiSchedule[]
  prefill: { question: string; device_id: number }
  myEmail: string
}

export function aiSchedulesQuery(q: string, deviceId: number) {
  return queryOptions({
    queryKey: ['assistant', 'schedules', q, deviceId],
    queryFn: () => apiGet<AiSchedulesPage>(`/assistant/schedules?q=${encodeURIComponent(q)}&device_id=${deviceId}`),
  })
}

export function aiSaveSchedule(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean; error?: string; id?: number }>('/assistant/schedules/save', fields)
}

export function aiDeleteSchedule(id: number) {
  return apiPost<{ ok: boolean }>('/assistant/schedules/delete', { id })
}

export function aiRunSchedule(id: number) {
  return apiPost<{ ok: boolean }>('/assistant/schedules/run', { id })
}

/** Banda de cost din sertar: conversatia de acum si luna, in forma firmei. */
export type AiSpendMoney = { form: 'usd' | 'credits' | 'hidden'; value: number | null; text: string }

export type AiSpend = {
  form: 'usd' | 'credits' | 'hidden'
  state: 'ok' | 'warn' | 'cheap' | 'stop'
  by: '' | 'company' | 'user'
  text: string
  warn_pct: number
  cheap_model: string
  conversation: AiSpendMoney
  month: AiSpendMoney
  allowance: AiSpendMoney
  unlimited: boolean
  pct: number | null
}

export function aiSpendQuery(conversationId: number, pulse: number, enabled: boolean) {
  return queryOptions({
    queryKey: ['assistant', 'spend', conversationId, pulse],
    queryFn: () => apiGet<AiSpend>(`/assistant/spend?conversation_id=${conversationId}`),
    enabled,
    staleTime: 30 * 1000,
  })
}
